import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useIntercom } from '@/hooks/useIntercom';
import type { IntercomUser } from '@/types/intercom';

interface IntercomContextValue {
  isLoading: boolean;
  isEnabled: boolean;
  error: Error | null;
}

const IntercomContext = createContext<IntercomContextValue>({
  isLoading: true,
  isEnabled: false,
  error: null,
});

interface IntercomProviderProps {
  children: ReactNode;
  user: IntercomUser | null;
  enabled?: boolean;
}

export function IntercomProvider({
  children,
  user,
  enabled = true,
}: Readonly<IntercomProviderProps>): React.ReactElement {
  const { isLoading, isInitialized, error } = useIntercom({ user, enabled });

  const contextValue = useMemo<IntercomContextValue>(
    () => ({ isLoading, isEnabled: isInitialized, error }),
    [isLoading, isInitialized, error],
  );

  return (
    <IntercomContext.Provider value={contextValue}>
      {children}
    </IntercomContext.Provider>
  );
}

export function useIntercomContext(): IntercomContextValue {
  return useContext(IntercomContext);
}
